import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../../lib/styles/props/component-style-props";
import {
  Box,
  ComponentMultiStyleConfig,
  Divider,
  Radio,
  RadioGroup,
  Text,
  useMultiStyleConfig,
  VStack,
} from "@chakra-ui/react";
import React, { ReactNode, useMemo } from "react";
import { RadioContent } from "../../../control";
import { messageId, ValueObjectMetaEnum } from "../../../../lib";
import { useMessageDictionary } from "../../../../hooks/use-message-dictionary";
import { CMFrameButtonPopupSearchCondition } from "../CMFrameButtonPopupSearchCondition/CMFrameButtonPopupSearchCondition";
import { hasValue } from "../../../../lib/util/common-util";

export type CMButtonPopupSearchConditionRadioProps<
  T extends [string, ...string[]],
> = {
  labelSubmit?: string;
  labelClear?: string;

  /**
   * クリアボタンの表示制御 trueの場合にクリアボタン非表示
   */
  hideClearButton?: boolean;

  /**
   * 値オブジェクトメタデータ
   */
  valueObjectMeta: ValueObjectMetaEnum<T>;

  internalValue: T[number] | null;
  /**
   * ラジオボタンで選択された値を呼び出し元に返却
   */
  onChangeInternalValue: (value: T[number] | null) => void;

  onSubmit: () => void;
  onClear: () => void;

  /**
   * ポップアップの上部に挿入するノード。
   */
  children?: ReactNode;
} & ComponentStyleProps;

/**
 * ラジオボタン検索条件のコンポーネント.
 *
 */
export const CMButtonPopupSearchConditionRadio = <
  T extends [string, ...string[]],
>({
  labelSubmit,
  labelClear,
  hideClearButton = false,

  valueObjectMeta,

  internalValue,
  onChangeInternalValue,

  onSubmit,
  onClear,

  children,

  sx,
  ...rest
}: CMButtonPopupSearchConditionRadioProps<T>) => {
  const styles = useMultiStyleConfig(
    "CMButtonPopupSearchConditionRadioStyles",
    {},
  );
  const dict = useMessageDictionary();

  // metaデータから取得した値をcontentの形に変換する
  const content: RadioContent = useMemo(
    () => ({
      options: valueObjectMeta.values.map((v) => ({
        label: dict(messageId(valueObjectMeta.dict[v as T[number]])) ?? "",
        value: v,
      })),
    }),
    [valueObjectMeta, dict],
  );

  /**
   * ラジオボタンの入力値を取得し、保持。
   */
  const handleChange = (changedValue: string | null) => {
    onChangeInternalValue(valueObjectMeta.toDomainObjectOrNull(changedValue));
  };

  return (
    <CMFrameButtonPopupSearchCondition
      labelSubmit={labelSubmit}
      labelClear={labelClear}
      hideClearButton={hideClearButton}
      onSubmit={onSubmit}
      onClear={onClear}
      {...rest}
      sx={{ ...styles.container, ...sx }}
    >
      {hasValue(children) && (
        <>
          <VStack>{children}</VStack>
          <Divider orientation={"horizontal"} sx={styles.divider} />
        </>
      )}
      <RadioGroup value={internalValue ?? undefined} onChange={handleChange}>
        <VStack sx={styles.radioStack}>
          {content.options.map((option, index) => (
            <Box key={index}>
              <Radio key={index} value={option.value} sx={styles.radio}>
                <Text sx={styles.radioLabel}>{option.label}</Text>
              </Radio>
            </Box>
          ))}
        </VStack>
      </RadioGroup>
    </CMFrameButtonPopupSearchCondition>
  );
};

export const CMButtonPopupSearchConditionRadioStyles: ComponentMultiStyleConfig =
  {
    parts: ["container", "radioStack", "radio", "radioLabel", "divider"],
    baseStyle: {
      container: {},
      radioStack: {
        alignItems: "flex-start",
      },
      radio: {
        mt: 0,
      },
      radioLabel: {},
      divider: { border: "solid 1px", borderColor: "gray.200", my: 3 },
    },
  };
